import React, { useMemo } from 'react';
import { useNavigation } from '@react-navigation/native';
import { AnimatedCircularProgress } from 'react-native-circular-progress';
import { View } from 'react-native';
import { getUserName } from '../../../common/utils/user/getUserName';
import { formatDate } from '../../../common/utils/utils';

import { useThemedStyles } from '../../hooks/useThemedStyles';

import { Text, ListItem, Icon } from '../../components';
import { StatusBadge } from '../../design/components';
import { useTheme } from '../../theme';


import {
	serviceRunStatus,
	numberOfCompletedMachines,
	SERVICE_RUN_STATES,
} from '../../../common/utils/vending-utils';


const getServiceRunProgress = ({ serviceRun, numeric, percentage }) => {

	const { serviceDockets: dockets } = serviceRun;
	const numberOfMachines = dockets ? dockets.length : 0;

	if (numeric) {
		return `${numberOfCompletedMachines(serviceRun)}/${numberOfMachines}`;
	}
	else if (percentage) {
		// Don't return NaN in unlikely but possible edge case
		if (numberOfMachines === 0) {
			return 0;
		}
		const completedMachinesAsInt = parseInt(numberOfCompletedMachines(serviceRun), 10);
		const completionPercentage = (completedMachinesAsInt / numberOfMachines) * 100;
		return completionPercentage;
	}
};



const STATUS_BADGE_TONES = {
	[SERVICE_RUN_STATES.IN_PROGRESS]: 'warning',
	[SERVICE_RUN_STATES.COMPLETED]: 'success',
};

// customProps.onRunPress(run): optional; returns true when it opened the run itself.
const ServiceRunListItem = ({
	item,
	customProps,
}) => {
	const navigation = useNavigation();
	const { theme: { ui: { colors } } } = useTheme();
	const onPress = () => {
		if (customProps?.onRunPress?.(item)) {
			return;
		}
		navigation.push('ServiceRunDetailView', { id: item.id });
	};

	const runStatus = serviceRunStatus(item) || '';

	const { route, driver } = item;

	const driverName = getUserName(driver);
	const routeName = route && route.name ? route.name : 'Service run';

	const plannedLabel = useMemo(() => (
		item.planned
			? `${formatDate(item.planned, 'DAY_MONTHWORD_YEAR')}`
			: 'No date'
	), [item.planned]);

	const vehicleName = item.vehicle?.name?.trim();
	const hasVehicle = Boolean(vehicleName);
	const vehicleLabel = vehicleName || 'No vehicle';

	const serviceRunProgressPercentage = useMemo(
		() => getServiceRunProgress({ serviceRun: item, percentage: true }),
		[item],
	);

	const isFaded = item.isFuture && serviceRunProgressPercentage === 0;

	const styles = useThemedStyles(createStyles);

	// Use a chip row (date / driver / vehicle) that can wrap naturally, while keeping vertical padding tight.
	const renderMetaChip = ({ icon, text, placeholder }) => (
		<View style={[styles.metaChip, placeholder && styles.metaChipPlaceholder]}>
			<Icon
				name={icon}
				size={16}
				color={placeholder ? colors.textFaint : colors.iconMuted}
				style={styles.metaIconSpacing}
			/>
			<Text
				style={[styles.metaText, placeholder && styles.metaTextPlaceholder]}
				numberOfLines={1}
				ellipsizeMode="tail"
			>
				{text}
			</Text>
		</View>
	);

	return (
		<ListItem onPress={onPress} style={[styles.listItem, isFaded && styles.fadedListItem]}>
			<View style={styles.contentRow}>
				<View style={styles.mainColumn}>
					{Boolean(runStatus) && (
						<StatusBadge
							label={runStatus}
							tone={STATUS_BADGE_TONES[runStatus] || 'neutral'}
							size="small"
							style={styles.serviceRunLabel}
						/>
					)}
					<Text style={styles.routeName} numberOfLines={2} ellipsizeMode="tail">
						{routeName}
					</Text>
					<View style={styles.metaRow}>
						{renderMetaChip({ icon: 'calendar', text: plannedLabel, placeholder: !item.planned })}
						{renderMetaChip({ icon: 'user', text: driverName, placeholder: !driver })}
						{renderMetaChip({ icon: 'truck', text: vehicleLabel, placeholder: !hasVehicle })}
					</View>
				</View>
				<View style={styles.progressContainer}>
					<AnimatedCircularProgress
						size={56}
						width={4}
						fill={serviceRunProgressPercentage}
						tintColor={runStatus === SERVICE_RUN_STATES.COMPLETED ? colors.success : colors.warning}
						backgroundColor={colors.border}
					>
						{
							() => (
								<Text style={styles.progressText}>
									{getServiceRunProgress({ serviceRun: item, numeric: true })}
								</Text>
							)
						}
					</AnimatedCircularProgress>
					<Text style={styles.progressLabel}>
						Machines serviced
					</Text>
				</View>
			</View>
		</ListItem>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	listItem: {
		marginHorizontal: space.gutter,
		marginTop: 0,
		marginBottom: space.sm,
		paddingHorizontal: 14,
		paddingVertical: 14,
	},
	fadedListItem: {
		opacity: 0.6,
		backgroundColor: colors.card,
		borderColor: colors.card,
	},
	contentRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.md,
	},
	mainColumn: {
		flex: 1,
		gap: space.sm,
	},
	serviceRunLabel: {
		alignSelf: 'flex-start',
	},
	routeName: {
		...type.rowTitleSemibold,
		color: colors.text,
	},
	metaRow: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		columnGap: space.lg,
		rowGap: space.xs,
	},
	metaChip: {
		flexDirection: 'row',
		alignItems: 'center',
		maxWidth: '100%',
	},
	metaChipPlaceholder: {
		opacity: 0.8,
	},
	metaIconSpacing: {
		marginRight: 6,
		width: 16,
	},
	metaText: {
		...type.secondary,
		color: colors.textSecondary,
		flexShrink: 1,
		minWidth: 0,
	},
	metaTextPlaceholder: {
		color: colors.textFaint,
	},
	progressContainer: {
		width: 78,
		alignItems: 'center',
		gap: space.xs,
	},
	progressText: {
		...type.secondarySemibold,
		color: colors.text,
	},
	progressLabel: {
		...type.caption,
		color: colors.textSecondary,
		textAlign: 'center',
	},
});

export default ServiceRunListItem;
